import { Avatar, Box, Button, Checkbox, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import SearchBar from '../../Common/Search/SearchBar';
import { theme } from '../../../theme';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import { TableWrapper } from './CandidateDetails.styles';
import { candidateType, PreboardingCandidatesFiltersType } from './AllCandidates';
import { TableDescription, TableRowStyled } from '../JobManagement/JobListing/JobListing.styles';
import Pagination from '../../Common/Pagination/pagination';
import { getPreboardingCandidates, notifyInternalTeams } from './preboardingApi';
import FilterPreboardingCandidates from './FilterPreboardingCandidates';
import { CheckBox } from '@mui/icons-material';
import NotifyInternalTeam from './NotifyInternalTeam';
import { emailTemplate } from '../CandidateManagement/CandidateContent/CandidateContent';
import { getEmailTemplates } from '../Admin/Communication/communicationApi';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { useGlobalContext } from '../../../Context/GlobalContext';
import { useLoadingBackdrop } from '../../../Context/LoadingBackdropContext';
import TeamNotificationStatus from './TeamNotificationStatus';


const HiredCandidates: React.FC = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const { tenant } = useParams<{ tenant: string }>();
  const [loading, setLoading] = useState(true);
  const [candidateData, setCandidateData] = useState<candidateType[]>([]);
  const [preboardingCandidateFilters, setPreboardingCandidateFilters] = useState<PreboardingCandidatesFiltersType>({
    recruiterName: '',
    department: '',
    jobTitle: '',
    location: ''
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRender = useRef(true);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [isOpenNotifyInternalTeams, setIsOpenNotifyInternalTeams] = useState(false)
  const [emailData, setEmailData] = useState<emailTemplate[]>([])
  const [selectedCandidates, setSelectedCandidates] = useState<{ mapId: number, candidateName: string, jobTitle: string }[]>([])
  const { showSnackbar } = useSnackbar();
  const { userData } = useGlobalContext();
  const { show, hide } = useLoadingBackdrop();

  const columnsData = [
    'Candidate Name',
    'Assigned Recruiter',
    'Job Title',
    'Department',
    'Location',
    'Team Notification Status',
  ];
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (rows: number) => {
    setRowsPerPage(rows);
    setPage(1);
  };

  const fetchPreboardingCandidates = async (filters: PreboardingCandidatesFiltersType, page: number, rowsPerPage: number, searchQuery: string) => {
    setLoading(true)
    try {
      const payLoad = {
        page: page,
        rowsPerPage: rowsPerPage,
        CandidateName: searchQuery,
        RecruiterName: filters.recruiterName,
        Department: filters.department,
        PreboardingStatus: ["Hired"],
        JobTitle: filters.jobTitle,
        Location: filters.location
      }
      const response = await getPreboardingCandidates(tenant, payLoad)
      if (response) {
        setCandidateData(response.Data)
        setTotalCandidates(response.Total)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchPreboardingCandidates(preboardingCandidateFilters, page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, tenant]);

  const handleCloseFilterDialog = () => {
    setIsFilterDialogOpen(false);
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return; // 🚫 Skip on initial render
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const updatedFilters = {
        ...preboardingCandidateFilters,
      };
      fetchPreboardingCandidates(updatedFilters, page, rowsPerPage, searchQuery);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  const hasActiveFilters = (filters: typeof preboardingCandidateFilters): boolean => {
    return (
      filters.recruiterName.trim() !== '' ||
      filters.department.trim() !== '' ||
      (filters.filterByStatus?.length ?? 0) > 0 ||
      filters.pendingWithFilter !== null ||
      (filters.jobTitle?.length ?? 0) > 0 ||
      (filters.location?.length ?? 0) > 0
    );
  };

  const handleApplyFilters = (newFilters: any) => {
    const isFiltered = hasActiveFilters(newFilters);
    if (isFiltered) {
      setPreboardingCandidateFilters(newFilters)
      fetchPreboardingCandidates(newFilters, 1, 10, searchQuery)
    }

    handleCloseFilterDialog();
  }

  const handleClearFilterAPI = async () => {
    const clearedFilters = {
      recruiterName: '',
      department: '',
      filterByStatus: [],
      waitingOnFilter: null,
      jobTitle: '',
      location: ''
    };
    setPreboardingCandidateFilters(clearedFilters);
    fetchPreboardingCandidates(clearedFilters, 1, 10, searchQuery);
  }

  const fetchEmailTemplates = async () => {
    const result = await getEmailTemplates(tenant)
    setEmailData(result)
  }

  useEffect(() => {
    fetchEmailTemplates()
  }, [tenant]);

  const handleSelectCandidate = (
    mapId: number,
    candidateName: string,
    jobTitle: string
  ) => {
    setSelectedCandidates((prev) => {
      // check if already selected
      const exists = prev.some((c) => c.mapId === mapId);

      if (exists) {
        // remove candidate
        return prev.filter((c) => c.mapId !== mapId);
      } else {
        // add candidate
        return [
          ...prev,
          {
            mapId,
            candidateName,
            jobTitle,
          },
        ];
      }
    });
  };

  const handleSelectAllCandidates = async () => {
    const payLoad = {
      page: page,
      rowsPerPage: rowsPerPage,
      CandidateName: searchQuery,
      RecruiterName: preboardingCandidateFilters.recruiterName,
      Department: preboardingCandidateFilters.department,
      PreboardingStatus: ["Hired"],
      JobTitle: preboardingCandidateFilters.jobTitle,
      Location: preboardingCandidateFilters.location
    }
    const response = await getPreboardingCandidates(tenant, payLoad)
    if (response) {
      const mappedSelectedData = response.Data
        .filter((candidate: candidateType) =>
          !candidate.InternalTeamsNotification.every((notif) => notif.IsSent)
        )
        .map((candidate: candidateType) => ({
          mapId: candidate.MapID,
          candidateName: candidate.CandidateName,
          jobTitle: candidate.JobTitle,
        }));

      setSelectedCandidates(mappedSelectedData)
    }
  }

  const isSelectAll =
    candidateData.length > 0 &&
    candidateData.every((c) =>
      selectedCandidates.some((sc) => sc.mapId === c.MapID)
    );


  const sendNotify = async (payLoad: any) => {
    try {
      show()
      const response = await notifyInternalTeams(tenant, payLoad)
      if (response) {
        showSnackbar(response.Message, response.Status ? "success" : "error")
        if (response.Status) {
          fetchPreboardingCandidates(preboardingCandidateFilters, 1, 10, searchQuery)
          setSelectedCandidates([])
        }
      }
    } catch (error) {
      showSnackbar("Failed to notification.Please try again later.")
      console.log(error)
    } finally {
      setIsOpenNotifyInternalTeams(false)
      hide()
    }
  }

  return (
    <Box mt={2.5} width={'100%'}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mb: 2 }}>
        <SearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          placeholder="Search by Candidate Name"
        />
        <Button variant="outlined" sx={{ color: theme.palette.primary.main, borderColor: theme.palette.primary.main }}
          onClick={() => setIsFilterDialogOpen(true)}
        >Filter & Sort</Button>
        <Button
          variant="contained"
          startIcon={<NotificationsActiveOutlinedIcon />}
          sx={{
            borderRadius: 1,
            textTransform: 'none',
            fontWeight: 500,
            bgcolor: theme.palette.primary.main,
          }}
          onClick={() => {
            if (selectedCandidates.length === 0) {
              showSnackbar("Please select atleast one candidate to notify internal teams.", "error")
              return
            }
            setIsOpenNotifyInternalTeams(true)
          }}
        >
          Notify Internal Teams
        </Button>
      </Box>

      <FilterPreboardingCandidates
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        onApply={handleApplyFilters}
        filters={preboardingCandidateFilters}
        handleClearFilterAPI={handleClearFilterAPI}
        filterFor='OnboardedCandidates'
      />

      <TableWrapper >
        <Box sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{
                  paddingLeft: 0,
                  paddingRight: 0,
                  textAlign: 'center'
                }}>
                  <Checkbox
                    checked={isSelectAll}
                    onChange={async (e) => {
                      if (e.target.checked) {
                        await handleSelectAllCandidates(); // ✅ select all
                      } else {
                        setSelectedCandidates([]); // ✅ clear all when unchecked
                      }
                    }}
                    inputProps={{ 'aria-label': 'select all candidates' }}
                    disabled={candidateData.length === 0}
                  />
                </TableCell>
                {columnsData.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      color: theme.palette.text.secondary,
                      padding: '8px 12px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '13.89vw',
                      minWidth: '6.94vw',
                      textAlign: ['team notification status'].includes(column.toLowerCase()) ? 'center' : 'left',
                    }}
                  >
                    <Typography variant='caption'>
                      {column.charAt(0).toUpperCase() + column.slice(1).replace(/_/g, ' ')}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {
                loading ? (
                  <TableRow>
                    <TableCell colSpan={columnsData.length} style={{ textAlign: 'center' }}>
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : candidateData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columnsData.length} style={{ textAlign: 'center', fontWeight: 'bold' }}>
                      No Candidate Data Available
                    </TableCell>
                  </TableRow>
                ) : (
                  candidateData.map((c, idx) => (
                    <TableRowStyled key={idx} iseven={(idx % 2 === 0).toString()}>
                      <TableDescription
                        sx={{
                          paddingLeft: 0,
                          paddingRight: 0,
                          textAlign: 'center'
                        }}>
                        <Checkbox
                          checked={selectedCandidates.some((sc) => sc.mapId === c.MapID)}
                          onChange={() =>
                            handleSelectCandidate(
                              c.MapID,
                              c.CandidateName,
                              c.JobTitle
                            )
                          }
                          disabled={c.InternalTeamsNotification.every((e) => e.IsSent)}
                          inputProps={{ "aria-label": `select candidate ${c.CandidateName}` }}
                        />

                      </TableDescription>
                      <TableDescription>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Avatar sx={{ width: 32, height: 32, bgcolor: '#e0e0e0' }}>
                            {c.CandidateName[0]}
                          </Avatar>
                          <Typography
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 500,
                              cursor: 'pointer',
                              textDecoration: 'underline'
                            }}
                            variant="body2"
                          >
                            {c.CandidateName}
                          </Typography>
                        </Box>
                      </TableDescription>
                      <TableDescription>{c.RecruiterName}</TableDescription>
                      <TableDescription>{c.JobTitle}</TableDescription>
                      <TableDescription>
                        {c.Department}
                      </TableDescription>
                      <TableDescription>
                        {c.Location || '-'}
                      </TableDescription>
                      <TableDescription>
                        <TeamNotificationStatus statusData={c.InternalTeamsNotification} />
                      </TableDescription>
                    </TableRowStyled>
                  ))
                )
              }

            </TableBody>
          </Table>
        </Box>

        {
          totalCandidates > 0 && (
            <Box sx={{ mt: '2.5vh', display: 'flex', mb: 2, mx: 2, justifyContent: 'center' }}>
              <Pagination
                totalRows={totalCandidates}
                rowsPerPage={rowsPerPage}
                currentPage={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          )
        }

      </TableWrapper>

      <NotifyInternalTeam
        open={isOpenNotifyInternalTeams}
        onClose={() => { setIsOpenNotifyInternalTeams(false) }}
        templatesDropdown={emailData}
        recruiterName={userData.UserName}
        candidatesList={selectedCandidates}
        sendNotify={sendNotify}
      />
    </Box>
  );
};

export default HiredCandidates;